import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useAllUnits } from "../../../features/units/queries";
import { fetchUnitHistory, TYPE_LABEL } from "../../../features/unitNotifications/unitNotifications";
import { alertRows, alertSummary, type AlertRow } from "../../../features/unitNotifications/alertRows";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportActionButton from "../components/ReportActionButton";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { ReportStatCard } from "../components/ReportCharts";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import { toLocalInputValue } from "../utils/reportDateFormat";

const DAY_MS = 86_400_000;
// El API admite hasta 31 días por consulta (unitNotificationsController).
const MAX_RANGE_DAYS = 31;

const COLUMNS: ReportTableColumn<AlertRow>[] = [
  { key: "fecha", header: "Fecha y hora" },
  { key: "unidad", header: "Unidad" },
  { key: "tipo", header: "Tipo de alerta" },
  { key: "alerta", header: "Alerta" },
  { key: "conductor", header: "Conductor" },
  { key: "velocidad", header: "Velocidad" },
  { key: "ubicacion", header: "Ubicación" },
];

/**
 * Historial de notificaciones de unidad de la compañía (en Mapon: «Alertas»).
 * Sólo lo que alguien tenía activado (decisión de Daniel, 26-oct). Con
 * ?vehicle=<id> abre filtrado por esa unidad (atajo desde la tarjeta del mapa).
 */
export default function AlertsSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const companyId = useCompaniesStore((s) => s.companyId);
  const { data: units = [] } = useAllUnits();
  const [params] = useSearchParams();

  const [start, setStart] = useState(() => toLocalInputValue(new Date(Date.now() - 7 * DAY_MS)));
  const [end, setEnd] = useState(() => toLocalInputValue(new Date()));
  const [vehicleId, setVehicleId] = useState(params.get("vehicle") ?? "");
  const [type, setType] = useState("");
  // Lo consultado: los filtros sólo aplican al tocar «Consultar». Con ?vehicle
  // se consulta de inmediato (viene de la tarjeta).
  const [query, setQuery] = useState<{ from: string; to: string; vehicleId: string; type: string } | null>(() =>
    params.get("vehicle") ? { from: new Date(start).toISOString(), to: new Date(end).toISOString(), vehicleId: params.get("vehicle")!, type: "" } : null,
  );
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);

  const rangeDays = (new Date(end).getTime() - new Date(start).getTime()) / DAY_MS;
  const rangeError = rangeDays > MAX_RANGE_DAYS ? `El rango admite hasta ${MAX_RANGE_DAYS} días` : rangeDays < 0 ? "La fecha final es anterior a la inicial" : null;

  const { data, isFetching, isError } = useQuery({
    queryKey: ["unitNotifications", "history", companyId, query],
    queryFn: () => fetchUnitHistory(companyId!, { from: query!.from, to: query!.to, vehicleId: query!.vehicleId || null, types: query!.type ? [query!.type] : undefined }),
    enabled: !!companyId && !!query,
    staleTime: 60_000,
  });

  const unitNameById = useMemo(() => {
    const m = new Map<string, string>();
    (units as any[]).forEach((u) => m.set(u.id, [u.name, u.plate].filter(Boolean).join(" · ") || u.id));
    return m;
  }, [units]);

  const rows = useMemo(
    () => alertRows(data?.items ?? [], (n) => (n.vehicle_id && unitNameById.get(n.vehicle_id)) || n.vehicle_name || n.device_id || "—"),
    [data, unitNameById],
  );
  const summary = useMemo(() => alertSummary(data?.items ?? []), [data]);

  const onConsult = () => setQuery({ from: new Date(start).toISOString(), to: new Date(end).toISOString(), vehicleId, type });

  const onDownload = async (kind: "pdf" | "excel") => {
    setExporting(kind);
    try {
      const meta = {
        title: "Alertas",
        period: `${new Date(query!.from).toLocaleString("es-MX")} — ${new Date(query!.to).toLocaleString("es-MX")}`,
        fileName: `alertas_${new Date().toISOString().slice(0, 10)}.${kind === "pdf" ? "pdf" : "xlsx"}`,
      };
      if (kind === "pdf") exportHistoricToPdf(rows, COLUMNS, meta);
      else await exportHistoricToExcel(rows, COLUMNS, meta);
    } finally {
      setExporting(null);
    }
  };

  const input = `rounded-md border px-2 py-2 text-sm ${isDarkMode ? "bg-white/5 border-dark-border text-white" : "bg-white border-gray-300"}`;
  const muted = themeClasses.textSecondary;

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4 flex flex-col gap-4">
      <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-3 rounded-lg border p-3 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <label className={`text-xs ${themeClasses.text}`}>Periodo</label>
            <ReportDateRangePicker startValue={start} endValue={end} onChangeStart={setStart} onChangeEnd={setEnd} endError={rangeError ?? undefined} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="alerts-unit" className={`text-xs ${themeClasses.text}`}>Unidad</label>
            <select id="alerts-unit" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className={`${input} min-w-52`}>
              <option value="">Toda la flota</option>
              {(units as any[]).map((u) => (
                <option key={u.id} value={u.id}>
                  {unitNameById.get(u.id)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="alerts-type" className={`text-xs ${themeClasses.text}`}>Tipo de alerta</label>
            <select id="alerts-type" value={type} onChange={(e) => setType(e.target.value)} className={`${input} min-w-48`}>
              <option value="">Todos los tipos</option>
              {Object.entries(TYPE_LABEL).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <ReportActionButton type="button" onClick={onConsult} disabled={!companyId || !!rangeError || isFetching}>
          Consultar
        </ReportActionButton>
      </div>

      {!companyId && <p className={`text-sm ${muted}`}>Selecciona una compañía para ver sus alertas.</p>}
      {isFetching && <ReportLoadingPanel isDarkMode={isDarkMode} icon="ri-notification-3-line" title="Buscando alertas…" subtitle="Notificaciones de unidad del periodo" />}
      {isError && <p className="text-sm text-red-500">No se pudieron cargar las alertas.</p>}

      {data && !isFetching && (
        <div className={`flex flex-col gap-4 rounded-lg border p-3 sm:p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {summary.total} alerta(s)
              {data.truncated && <span className="text-amber-500"> · se muestran las 1000 más recientes: acota el periodo o la unidad</span>}
            </span>
            {rows.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(["pdf", "excel"] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => onDownload(kind)}
                    disabled={exporting !== null}
                    className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                  >
                    {exporting === kind ? "Generando..." : `Descargar ${kind === "pdf" ? "PDF" : "Excel"}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <ReportStatCard icon="ri-notification-3-line" label="Alertas" value={String(summary.total)} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-alarm-warning-line" label="Críticas (pánico, paro, desvío)" value={String(summary.critical)} isDarkMode={isDarkMode} iconColor="text-red-500" />
            <ReportStatCard icon="ri-error-warning-line" label="Advertencias" value={String(summary.warning)} isDarkMode={isDarkMode} iconColor="text-amber-500" />
            <ReportStatCard icon="ri-bar-chart-line" label="Más frecuente" value={summary.top ? `${summary.top[0]} (${summary.top[1]})` : "—"} isDarkMode={isDarkMode} />
          </div>

          <ReportPointsTable
            data={rows}
            columns={COLUMNS}
            isDarkMode={isDarkMode}
            pageSize={50}
            emptyText="Sin alertas en el periodo. Sólo se registran las que algún usuario tiene activadas."
          />
        </div>
      )}
    </div>
  );
}
